import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft, FolderOpen, FileText, CheckCircle, Clock,
  X, Loader2, AlertTriangle, Trash2, Download, FileCheck, ShieldAlert, ChevronDown, ChevronRight
} from 'lucide-react';
import DataPurgeDialog from '../components/DataPurgeDialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BREX_IMAGE = "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/hd7tcvfm_BREXValidation.png";

const BREXValidationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const brexInputRef = useRef(null);
  const dmInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      axios.post(`${API}/history/tool-usage`, { tool_name: 'BREX Validation' }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, []);

  const initialProject = location.state?.project;
  const initialProjectId = location.state?.projectId;

  const projects = useProjects();
  const [selectedProject, setSelectedProject] = useState(initialProjectId || 'none');
  const [currentProjectName, setCurrentProjectName] = useState(initialProject?.title || null);
  const [brexFiles, setBrexFiles] = useState([]);
  const [dmFiles, setDmFiles] = useState([]);
  const [brexFolderName, setBrexFolderName] = useState('');
  const [dmFolderName, setDmFolderName] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [sourceCollapsed, setSourceCollapsed] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchHistory(); }, [token, selectedProject]);

  const fetchHistory = async () => {
    try {
      const query = selectedProject !== 'none' ? `?project_id=${selectedProject}` : '';
      const res = await axios.get(`${API}/brex/history${query}`, { headers });
      setHistory(res.data);
    } catch { }
    finally { setIsLoadingHistory(false); }
  };

  const handleBrexFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    const xmlFiles = files.filter(f => f.name.toLowerCase().endsWith('.xml') && f.name.includes('022'));
    if (xmlFiles.length === 0) {
      toast.error('No BREX modules found (files with "022" in filename)');
      return;
    }
    const folderPath = files[0]?.webkitRelativePath?.split('/')[0] || 'BREX Folder';
    setBrexFolderName(folderPath);
    Promise.all(xmlFiles.map(file => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve({ filename: file.name, content: ev.target.result });
      reader.readAsText(file);
    }))).then(fileData => {
      setBrexFiles(fileData);
      toast.success(`Loaded ${fileData.length} BREX module(s) from "${folderPath}"`);
    });
  };

  const handleDmFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    const xmlFiles = files.filter(f => f.name.toLowerCase().endsWith('.xml'));
    const nonXmlFiles = files.filter(f => !f.name.toLowerCase().endsWith('.xml') && !f.name.startsWith('.'));
    if (xmlFiles.length === 0) {
      toast.error('No XML data modules found in selected folder. Data Modules must be XML files.');
      return;
    }
    if (nonXmlFiles.length > 0) {
      toast.warning(`${nonXmlFiles.length} non-XML file(s) ignored. Only XML files are processed.`);
    }
    const folderPath = files[0]?.webkitRelativePath?.split('/')[0] || 'DM Folder';
    setDmFolderName(folderPath);
    Promise.all(xmlFiles.map(file => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve({ filename: file.name, content: ev.target.result });
      reader.readAsText(file);
    }))).then(fileData => {
      setDmFiles(fileData);
      toast.success(`Loaded ${fileData.length} data module(s) from "${folderPath}"`);
    });
  };

  const foldersAreDifferent = brexFolderName && dmFolderName && brexFolderName !== dmFolderName;
  const canValidate = (isAdmin || selectedProject !== 'none') && brexFiles.length > 0 && dmFiles.length > 0 && foldersAreDifferent;

  const handleValidate = async () => {
    if (!foldersAreDifferent) {
      toast.error('BREX and Data Module folders must be different');
      return;
    }
    setIsValidating(true);
    try {
      const res = await axios.post(`${API}/brex/validate`, {
        project_id: selectedProject === 'none' ? null : selectedProject,
        brex_files: brexFiles,
        dm_files: dmFiles
      }, { headers });

      setValidationResult(res.data);
      if (res.data.status === 'version_mismatch') {
        toast.error(res.data.message);
      } else {
        setBrexFiles([]);
        setDmFiles([]);
        setBrexFolderName('');
        setDmFolderName('');
        setSourceCollapsed(true);
        fetchHistory();
        setShowPurgeDialog(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally { setIsValidating(false); }
  };

  const viewHistoryDetail = async (item) => {
    try {
      const res = await axios.get(`${API}/brex/history/${item.id}`, { headers });
      setSelectedHistory(res.data);
      setValidationResult({
        status: 'completed',
        brex_summary: { total: res.data.total_dm, passed: res.data.brex_passed, failed: res.data.brex_failed },
        schema_summary: { total: res.data.total_dm, passed: res.data.schema_passed, failed: res.data.schema_failed },
        brex_results: res.data.brex_results,
        schema_results: res.data.schema_results,
        history_id: res.data.id
      });
      setSourceCollapsed(true);
    } catch { toast.error('Failed to load history'); }
  };

  const deleteHistoryItem = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/brex/history/${id}`, { headers });
      toast.success('History deleted');
      fetchHistory();
    } catch { toast.error('Failed to delete'); }
  };

  const handleExport = async (exportType) => {
    const historyId = validationResult?.history_id || selectedHistory?.id;
    if (!historyId) return;
    try {
      const res = await axios.get(`${API}/brex/export/${historyId}?export_type=${exportType}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `brex_${exportType}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleDownloadAllZip = async (historyId, e) => {
    if (e) e.stopPropagation();
    const hid = historyId || validationResult?.history_id || selectedHistory?.id;
    if (!hid) return;
    try {
      const res = await axios.get(`${API}/brex/export-all-zip/${hid}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'brex_validation_reports.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('All reports downloaded');
    } catch { toast.error('Download failed'); }
  };

  const clearFiles = () => {
    setBrexFiles([]); setDmFiles([]);
    setBrexFolderName(''); setDmFolderName('');
    setValidationResult(null);
    setSourceCollapsed(false);
  };

  return (
    <div className="space-y-8" data-testid="brex-validation-page">
      {/* Hidden folder inputs - refs set via callback to ensure webkitdirectory works */}
      <input
        type="file"
        ref={(el) => { brexInputRef.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }}
        onChange={handleBrexFolderSelect}
        className="hidden"
        multiple
      />
      <input
        type="file"
        ref={(el) => { dmInputRef.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }}
        onChange={handleDmFolderSelect}
        className="hidden"
        multiple
      />

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(selectedProject !== 'none' ? `/dashboard/projects/${selectedProject}` : '/dashboard/projects')} className="text-slate-600 hover:text-slate-900" data-testid="back-to-tools-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />{selectedProject !== 'none' ? 'Back to Project' : 'Back to Projects'}
        </Button>
      </div>

      {/* Tool Header */}
      <div className="flex flex-col md:flex-row items-start gap-6">
        <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 shadow-lg">
          <img src={BREX_IMAGE} alt="BREX Validation" className="w-full h-full object-contain" />
        </div>
        <div className="space-y-2 flex-1">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            BREX Validation
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Validate data modules against Business Rules Exchange (BREX) specifications and S1000D schema requirements.
          </p>
        </div>
      </div>

      {/* Project Selection */}
      <Card className={`border-2 ${selectedProject === 'none' ? 'border-violet-300 bg-violet-50/30' : 'border-slate-200'}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Select Project {!isAdmin && <span className="text-red-500">*</span>}</label>
            <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); setCurrentProjectName(projects.find(p => p.id === v)?.title || null); }}>
              <SelectTrigger className="w-[300px]" data-testid="brex-project-select"><SelectValue placeholder="Select a project..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-slate-400">{isAdmin ? '-- No Project (Testing) --' : '-- Select a project --'}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedProject !== 'none' && currentProjectName && (
              <Badge variant="secondary" className="bg-violet-100 text-violet-700">{currentProjectName}</Badge>
            )}
            {isAdmin && selectedProject === 'none' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Testing Mode</Badge>}
          </div>
          {!isAdmin && selectedProject === 'none' && <p className="text-sm text-violet-700 mt-2">A project must be selected to run validation and save history</p>}
        </CardContent>
      </Card>

      {/* BREX Validation Button - Top */}
      <div className="flex items-center gap-3">
        <Button
          className="bg-slate-900 hover:bg-slate-800 px-8 py-5 text-base"
          disabled={!canValidate || isValidating}
          onClick={handleValidate}
          data-testid="brex-validate-btn"
        >
          {isValidating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Validating...</> : <><FileCheck className="w-4 h-4 mr-2" />BREX Validation</>}
        </Button>
        {(brexFiles.length > 0 || dmFiles.length > 0) && (
          <Button variant="outline" onClick={clearFiles}>Clear All</Button>
        )}
        {!canValidate && !isAdmin && selectedProject === 'none' && (
          <p className="text-sm text-violet-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />Select a project first
          </p>
        )}
        {!canValidate && (isAdmin || selectedProject !== 'none') && (brexFiles.length === 0 || dmFiles.length === 0) && (
          <p className="text-sm text-slate-500 flex items-center gap-2">
            Select both BREX and Data Module folders to enable validation
          </p>
        )}
        {brexFolderName && dmFolderName && !foldersAreDifferent && (
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />BREX and Data Module folders must be different
          </p>
        )}
      </div>

      {/* Version mismatch message */}
      {validationResult?.status === 'version_mismatch' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 font-medium flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />{validationResult.message}
          </p>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Folder Selection Section - Collapsible */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setSourceCollapsed(!sourceCollapsed)}
          >
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {sourceCollapsed ? <ChevronRight className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              <FolderOpen className="w-5 h-5 text-violet-600" />Source Selection
              {sourceCollapsed && brexFiles.length > 0 && dmFiles.length > 0 && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({brexFiles.length} BREX, {dmFiles.length} DMs)
                </span>
              )}
            </CardTitle>
            {!sourceCollapsed && <CardDescription>Select BREX modules folder and Data Modules folder</CardDescription>}
          </CardHeader>
          {!sourceCollapsed && (
            <CardContent className="space-y-6">
            {/* BREX Folder */}
            <div
              className="border-2 border-dashed border-slate-200 rounded-xl p-6 hover:border-violet-300 hover:bg-violet-50/30 transition-colors cursor-pointer"
              onClick={() => brexInputRef.current?.click()}
              data-testid="brex-upload-area"
            >
              <div className="flex items-center gap-4">
                <FolderOpen className="w-10 h-10 text-slate-400" />
                <div className="flex-1">
                  <p className="font-medium text-slate-700">BREX Selection</p>
                  <p className="text-sm text-slate-500">Select the folder containing BREX modules (XML files with information code 022 in filename)</p>
                </div>
              </div>
            </div>
            {brexFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">BREX Modules ({brexFiles.length}) - <span className="text-violet-600">{brexFolderName}</span></p>
                <div className="flex flex-wrap gap-2">
                  {brexFiles.map((f, i) => (
                    <span key={i} className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-sm flex items-center gap-2">
                      <FileText className="w-3 h-3" />{f.filename}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* DM Folder */}
            <div
              className="border-2 border-dashed border-slate-200 rounded-xl p-6 hover:border-violet-300 hover:bg-violet-50/30 transition-colors cursor-pointer"
              onClick={() => dmInputRef.current?.click()}
              data-testid="dm-upload-area"
            >
              <div className="flex items-center gap-4">
                <FileText className="w-10 h-10 text-slate-400" />
                <div className="flex-1">
                  <p className="font-medium text-slate-700">Data Modules</p>
                  <p className="text-sm text-slate-500">Select the folder containing the data modules to be checked (XML files only)</p>
                </div>
              </div>
            </div>
            {dmFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Data Modules ({dmFiles.length}) - <span className="text-violet-600">{dmFolderName}</span></p>
                <div className="flex flex-wrap gap-2">
                  {dmFiles.map((f, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm flex items-center gap-2">
                      <FileText className="w-3 h-3" />{f.filename}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
          )}
        </Card>

        {/* History Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Clock className="w-5 h-5 text-slate-600" />History
            </CardTitle>
            <CardDescription>Recent validations</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingHistory ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No validation history yet</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {history.map((item) => (
                    <div key={item.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors group" onClick={() => viewHistoryDetail(item)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {item.project_title && <p className="text-xs text-violet-600 font-medium truncate">{item.project_title}</p>}
                          <p className="text-sm text-slate-700">{item.total_dm} DMs checked</p>
                          <p className="text-xs text-slate-500">{new Date(item.timestamp).toLocaleString()}</p>
                          <p className="text-xs text-slate-500">
                            BREX: {item.brex_passed}P/{item.brex_failed}F | Schema: {item.schema_passed}P/{item.schema_failed}F
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge variant={item.brex_failed === 0 && item.schema_failed === 0 ? "default" : "destructive"}>
                            {item.brex_failed === 0 && item.schema_failed === 0 ? 'PASS' : 'FAIL'}
                          </Badge>
                          <button onClick={(e) => handleDownloadAllZip(item.id, e)} className="opacity-0 group-hover:opacity-100 p-1 hover:text-violet-600 transition-opacity" title="Download Reports">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={(e) => deleteHistoryItem(item.id, e)} className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-opacity" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inline Validation Results */}
      {validationResult && validationResult.status === 'completed' && (
        <Card className="border-2 border-violet-200" data-testid="validation-results">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <FileCheck className="w-5 h-5 text-violet-600" />Validation Results
              </CardTitle>
              <CardDescription>BREX and Schema validation analysis</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setValidationResult(null); setSelectedHistory(null); setSourceCollapsed(false); }}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Export Buttons */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-700">Export Options</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { type: 'all_brex_schema', label: 'Export All - BREX & Schema' },
                  { type: 'all_brex', label: 'Export All - BREX' },
                  { type: 'all_schema', label: 'Export All - Schema' },
                  { type: 'passed_brex_schema', label: 'Export Passed - BREX & Schema' },
                  { type: 'passed_brex', label: 'Export Passed - BREX' },
                  { type: 'passed_schema', label: 'Export Passed - Schema' },
                  { type: 'failed_brex_schema', label: 'Export Failed - BREX & Schema' },
                  { type: 'failed_brex', label: 'Export Failed - BREX' },
                  { type: 'failed_schema', label: 'Export Failed - Schema' },
                ].map(opt => (
                  <Button key={opt.type} variant="outline" size="sm" onClick={() => handleExport(opt.type)} className="text-xs">
                    <Download className="w-3 h-3 mr-1" />{opt.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Summaries side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* BREX Summary */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">BREX Validation</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">{validationResult.brex_summary.total}</p>
                    <p className="text-xs text-slate-600">Total Data Modules</p>
                  </div>
                  <div className="p-4 bg-green-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{validationResult.brex_summary.passed}</p>
                    <p className="text-xs text-green-600">Total Passed</p>
                  </div>
                  <div className="p-4 bg-red-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{validationResult.brex_summary.failed}</p>
                    <p className="text-xs text-red-600">Total Failed</p>
                  </div>
                </div>
              </div>

              {/* Schema Summary */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Schema Validation</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">{validationResult.schema_summary.total}</p>
                    <p className="text-xs text-slate-600">Total Data Modules</p>
                  </div>
                  <div className="p-4 bg-green-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{validationResult.schema_summary.passed}</p>
                    <p className="text-xs text-green-600">Total Passed</p>
                  </div>
                  <div className="p-4 bg-red-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{validationResult.schema_summary.failed}</p>
                    <p className="text-xs text-red-600">Total Failed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* BREX Failures Detail */}
            {validationResult.brex_results?.some(r => !r.passed) && (
              <div className="border rounded-lg">
                <div className="p-3 bg-red-50 border-b flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-600" />
                  <span className="font-medium text-red-800">BREX Failures</span>
                </div>
                <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
                  {validationResult.brex_results.filter(r => !r.passed).map((r, i) => (
                    <div key={i} className="p-3 bg-red-50/50 rounded border border-red-100">
                      <p className="text-sm font-medium text-slate-900 font-mono">{r.dmc}</p>
                      {r.failures.map((f, j) => (
                        <div key={j} className="ml-4 mt-2 text-sm text-slate-600 border-l-2 border-red-200 pl-3">
                          <span className="font-semibold text-red-600">{f.brdp_id}</span>
                          {f.brdp_title && <span className="ml-1">- {f.brdp_title}</span>}
                          {f.location && <p className="text-xs text-slate-500 mt-0.5">Location: <span className="font-mono">{f.location}</span></p>}
                          {f.message && <p className="text-sm text-red-600 mt-0.5">{f.message}</p>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Schema Failures Detail */}
            {validationResult.schema_results?.some(r => !r.passed) && (
              <div className="border rounded-lg">
                <div className="p-3 bg-amber-50 border-b flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-800">Schema Failures</span>
                </div>
                <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
                  {validationResult.schema_results.filter(r => !r.passed).map((r, i) => (
                    <div key={i} className="p-3 bg-amber-50/50 rounded border border-amber-100">
                      <p className="text-sm font-medium text-slate-900 font-mono">{r.dmc}</p>
                      {r.errors.map((err, j) => (
                        <p key={j} className="ml-4 mt-1 text-sm text-amber-700">{err}</p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Passed DMs */}
            {validationResult.brex_results?.some(r => r.passed) && (
              <div className="border rounded-lg">
                <div className="p-3 bg-green-50 border-b flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-800">
                    Passed ({validationResult.brex_results?.filter(r => r.passed).length || 0} BREX / {validationResult.schema_results?.filter(r => r.passed).length || 0} Schema)
                  </span>
                </div>
                <div className="p-3 max-h-[200px] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1">
                    {validationResult.brex_results?.filter(r => r.passed).map((r, i) => (
                      <p key={i} className="text-sm text-slate-700 font-mono truncate">{r.dmc}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <DataPurgeDialog open={showPurgeDialog} onAcknowledge={() => setShowPurgeDialog(false)} toolName="validation" />
    </div>
  );
};

export default BREXValidationPage;
